import { UNPLACED_BAND, type BandRow, type UnplacedRow } from '../model/orbital.js';
import type { Selection } from '../model/selection.js';

/**
 * The band strip — what the rings are not showing, and why (Phase 6).
 *
 * One cell per band under the canvas, and this is where the phase's first
 * question ended up. `orbital-map.js` can place a marker at exactly one kind of
 * position — an angle derived from a slot — and three of the wire's locations
 * name no slot: a `BAND` contact, an asset in `TRANSIT`, and a contact the wire
 * places nowhere at all. In turn 1 of the only scenario **every** hostile in
 * space is the first of those, so this is not an edge case; it is the enemy
 * picture, and the ring has no channel for it.
 *
 * Giving them a slot anyway would put them on a bin the seat has not earned —
 * indistinguishable, on screen, from a contact that really is at slot 07. So
 * they are stated here instead, in the console's own pixels, under a one-clause
 * heading that says which fact is missing. The argument is `model/orbital.ts`'s
 * and it is Phase 5's finding 5 applied a second time: *a sentence that is right
 * beats a mark that is wrong.*
 *
 * ## The strip is a board, not a caption
 *
 * The argument above was right and the proportions contradicted it. Every label
 * in here was set at 9px and every id at 10.5px — off the bottom of the design
 * system's own ramp, whose smallest step is the 10px data tag — so the surface
 * carrying the entire enemy picture was the smallest type on the page, under a
 * 287px canvas drawing seven friendly satellites whose positions the seat already
 * owns. Three changes, and none of them touches the rule:
 *
 * - **The ramp.** Labels are Barlow Condensed 600 / 11px / `.14em` like every
 *   other label in the console; ids are mono 12px like the roster row that names
 *   the same object; the detail beside them is the 10px data tag.
 * - **A header.** Four bare band names said nothing about what the strip *was*.
 *   `BANDS` names it, and the count beside it answers the question the strip
 *   exists to raise — *how many am I not seeing on the rings* — which previously
 *   required selecting each band in turn and reading the rail.
 * - **A selected row reads as selected.** Clicking a contact here filled the chip
 *   and the rail and left the row it came from unchanged, so the one surface that
 *   holds the enemy picture was the one surface that would not admit to holding
 *   the selection. It now carries the roster's own marker — cyan tint, 2px cyan
 *   edge — because it is the same act of picking, and DESIGN.md says a selection
 *   that is not cyan is a defect rather than a variation.
 *
 * The height for all of this came from `orders/QueueDock.tsx`, which held an
 * order card's 122px whether or not it had a card in it. Nothing was taken from
 * the canvas: the rings and the strip are both the board, and the operator needs
 * to read them together.
 *
 * ## What each cell says, and nothing more
 *
 * The band's name, its slot count and its debris level — the two numbers the
 * renderer's own header refuses to draw on the canvas — then the unplaced
 * objects. It does **not** count what is on the ring: those markers are an inch
 * above and the selection rail states the numbers when a band is selected.
 *
 * ## It follows the HOSTILE TRACKS switch
 *
 * One rule, both surfaces: a layer that hid contacts on the canvas and left them
 * listed underneath would make the switch mean two different things a foot
 * apart. That is also why the strip is not the place to argue about must-not #3
 * — a contact the wire cannot place is hidden by the *hostile* switch because it
 * is hostile, not because it is unplaced. The header's count obeys the same
 * switch, for the same reason: it counts the rows that are on screen, never the
 * rows the projection happens to hold.
 */

const LABEL = {
  fontFamily: 'var(--font-ui)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '.14em',
  color: 'var(--text-muted)',
} as const;

export function BandStrip(props: {
  readonly rows: readonly BandRow[];
  readonly hostile: boolean;
  readonly selected: string | null;
  /** The selected asset or contact, so a row here can say it is the one. */
  readonly selectedId: string | null;
  readonly onSelectBand: (band: string) => void;
  readonly onSelect: (selection: Selection) => void;
  readonly onBandContextMenu: (band: string, x: number, y: number) => void;
  readonly onUnitContextMenu: (id: string, x: number, y: number) => void;
}): JSX.Element {
  const shown = props.rows.reduce(
    (total, row) =>
      total +
      row.unplaced.reduce(
        (rows, group) =>
          rows + group.rows.filter((entry) => props.hostile || entry.side !== 'OPF').length,
        0,
      ),
    0,
  );

  return (
    <div
      data-testid="band-strip"
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        borderTop: '1px solid var(--border-strong)',
      }}
    >
      <div
        data-mat="chrome hdr"
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          height: 24,
          padding: '0 10px',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-data)',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '.14em',
            color: 'var(--text)',
          }}
        >
          BANDS
        </h2>
        <Pair label="NOT ON RINGS" value={String(shown)} />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 1,
          maxHeight: 186,
          overflowY: 'auto',
          background: 'var(--border)',
        }}
      >
        {props.rows.map((row) => (
          <Cell
            key={row.band}
            row={row}
            hostile={props.hostile}
            selected={props.selected === row.band}
            selectedId={props.selectedId}
            onSelectBand={props.onSelectBand}
            onSelect={props.onSelect}
            onBandContextMenu={props.onBandContextMenu}
            onUnitContextMenu={props.onUnitContextMenu}
          />
        ))}
      </div>
    </div>
  );
}

function Cell(props: {
  readonly row: BandRow;
  readonly hostile: boolean;
  readonly selected: boolean;
  readonly selectedId: string | null;
  readonly onSelectBand: (band: string) => void;
  readonly onSelect: (selection: Selection) => void;
  readonly onBandContextMenu: (band: string, x: number, y: number) => void;
  readonly onUnitContextMenu: (id: string, x: number, y: number) => void;
}): JSX.Element {
  const row = props.row;
  const placeable = row.band !== UNPLACED_BAND;
  const groups = row.unplaced
    .map((group) => ({
      ...group,
      rows: props.hostile ? group.rows : group.rows.filter((entry) => entry.side !== 'OPF'),
    }))
    .filter((group) => group.rows.length > 0);

  return (
    <div
      data-testid="band-cell"
      data-band={row.band}
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '7px 10px 9px',
        background: props.selected ? 'var(--surface-2)' : 'var(--surface)',
        // Accent, not primary: selecting a band picks something out, it does not
        // change the world. `RosterRow` settled this for the roster and the rule
        // is the system's — amber acts, cyan selects — so the strip, the chip it
        // raises in the status row and the rail it fills all say cyan together.
        borderTop: '2px solid ' + (props.selected ? 'var(--accent)' : 'transparent'),
      }}
    >
      <button
        type="button"
        data-testid="band-cell-select"
        // The fifth column is not a band and there is nothing to select: it is a
        // heading over contacts the wire places nowhere, and `map.bandSlots` has
        // no entry to resolve it against.
        disabled={!placeable}
        aria-current={props.selected ? 'true' : undefined}
        onClick={() => props.onSelectBand(row.band)}
        onContextMenu={(event) => {
          if (!placeable) return;
          event.preventDefault();
          props.onBandContextMenu(row.band, event.clientX, event.clientY);
        }}
        style={{
          display: 'block',
          padding: 0,
          background: 'transparent',
          border: 0,
          textAlign: 'left',
          fontFamily: 'var(--font-ui)',
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: '.14em',
          color: props.selected ? 'var(--accent)' : placeable ? 'var(--text)' : 'var(--text-3)',
          cursor: placeable ? 'pointer' : 'default',
        }}
      >
        {row.band}
      </button>

      {row.slots !== null && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <Pair label="SLOTS" value={String(row.slots)} />
          {row.debris !== null && (
            <Pair
              label="DEBRIS"
              value={String(row.debris)}
              // The rails' own thresholds, so the strip and `EnvironmentBlock`
              // colour the same number the same way.
              tone={row.debris >= 4 ? 'var(--red-text)' : row.debris > 0 ? 'var(--warn)' : null}
            />
          )}
        </div>
      )}

      {groups.map((group) => (
        <div key={group.reason} data-testid="unplaced-group" data-reason={group.reason}>
          <span style={{ ...LABEL, display: 'block', marginBottom: 3 }}>{group.reason}</span>
          {group.rows.map((entry) => (
            <UnplacedButton
              key={entry.id}
              row={entry}
              selected={props.selectedId === entry.id}
              onSelect={props.onSelect}
              onContextMenu={props.onUnitContextMenu}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function UnplacedButton(props: {
  readonly row: UnplacedRow;
  readonly selected: boolean;
  readonly onSelect: (selection: Selection) => void;
  readonly onContextMenu: (id: string, x: number, y: number) => void;
}): JSX.Element {
  const row = props.row;
  return (
    <button
      type="button"
      data-testid="unplaced-row"
      data-id={row.id}
      aria-current={props.selected ? 'true' : undefined}
      onClick={() => props.onSelect(row.selects)}
      onContextMenu={(event) => {
        event.preventDefault();
        props.onContextMenu(row.id, event.clientX, event.clientY);
      }}
      style={{
        /*
          Two lines, always — the shape `RosterRow` argued for and for the same
          reason, harder.

          A cell is 139px wide. An alias is 92px of that at the size it has to be
          read at, and the rung beside it was being given 19px for 37px of text —
          `FOUND` rendered as `F…` on every contact on the board. A transiting
          asset is worse: `BLUE-INTERCEPTOR-AURELIA` beside `GEO → T10` does not
          fit on one line at any size worth setting. Wrapping only the long ones
          would make half the rows one shape and half another, and this is a list
          that gets scanned rather than read.

          The 2px cyan edge and the tint are the roster's marker, because it is
          the same act of picking. The negative inset pulls the tint out to the
          cell's own padding so the strip does not grow a ladder of rectangles.
        */
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        margin: '0 -10px',
        padding: '1px 10px',
        background: props.selected ? 'var(--accent-tint)' : 'transparent',
        border: 0,
        borderLeft: '2px solid ' + (props.selected ? 'var(--accent)' : 'transparent'),
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.3,
          letterSpacing: '.04em',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: row.side === 'BLU' ? 'var(--blue)' : 'var(--red)',
        }}
      >
        {row.id}
      </span>
      {row.detail !== '' && (
        <span
          style={{
            fontFamily: 'var(--font-data)',
            fontSize: 10,
            lineHeight: 1.3,
            letterSpacing: '.14em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--text-3)',
          }}
        >
          {row.detail}
        </span>
      )}
    </button>
  );
}

function Pair(props: {
  readonly label: string;
  readonly value: string;
  readonly tone?: string | null;
}): JSX.Element {
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: 5, whiteSpace: 'nowrap' }}>
      <span style={LABEL}>{props.label}</span>
      <span
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: 12,
          fontWeight: 600,
          color: props.tone ?? 'var(--text-2)',
        }}
      >
        {props.value}
      </span>
    </span>
  );
}
